/**
 * useResetPasswordState — « Nouveau mot de passe » (`password/reset/:passwordToken`), ouvert depuis
 * le lien de réinitialisation : les trois champs, l'enregistrement, la suite.
 *
 * Le portage de `src/auth/ResetPasswordScreen.js` :
 *  - les champs se valident à l'envoi, puis à chaque frappe (`resetPasswordErrors`) ;
 *  - `POST api/password/reset` part comme le legacy (`usePasswordFormSubmit`), avec
 *    `{ email, token, password, password_confirmation }` — le jeton est celui de la route ;
 *  - enregistré → les félicitations « Enregistré » (au vouvoiement), puis l'écran `congrats`, qui
 *    reste legacy. Pas de connexion automatique : la personne se connecte ensuite elle-même ;
 *  - un refus (lien périmé, adresse inconnue) se lit au-dessus du bouton, dans les mots du serveur ;
 *  - la croix revient d'où l'on vient, sinon à l'accueil.
 */
import { useState } from "react"

import { authService } from "../../../../../../data/services/authService"
import {
    hasErrors,
    PASSWORD_RESET_DONE,
    resetPasswordErrors,
    type ResetPasswordValues,
} from "../../../../view-models/auth/Password/passwordFormViewModel"
import { useBackOr } from "../../../hooks/useBackOr"
import { usePasswordFormSubmit } from "../usePasswordFormSubmit"

const RESET_PASSWORD_FAILED = "Le mot de passe n'a pas pu être enregistré. Réessayez dans un instant."

const NO_VALUES: ResetPasswordValues = { email: "", password: "", passwordConfirmation: "" }

export function useResetPasswordState(passwordToken: string | null | undefined) {
    const onClose = useBackOr("menu")
    const [values, setValues] = useState(NO_VALUES)
    const change = (field: keyof ResetPasswordValues) => (value: string) =>
        setValues((current) => ({ ...current, [field]: value }))

    const { submitted, serverError, submitting, submit } = usePasswordFormSubmit({
        service: authService.resetPassword,
        done: PASSWORD_RESET_DONE,
        fallback: RESET_PASSWORD_FAILED,
    })

    const errors = resetPasswordErrors(values)
    const shown = submitted ? errors : {}

    return {
        ...values,
        onChangeEmail: change("email"),
        onChangePassword: change("password"),
        onChangePasswordConfirmation: change("passwordConfirmation"),
        emailError: shown.emailError,
        passwordError: shown.passwordError,
        passwordConfirmationError: shown.passwordConfirmationError,
        serverError,
        submitting,
        onSubmit: () =>
            submit(
                {
                    email: values.email,
                    token: passwordToken,
                    password: values.password,
                    password_confirmation: values.passwordConfirmation,
                },
                hasErrors(errors)
            ),
        onClose,
    }
}
